/**
 * Beneficios de días (días administrativos, medio día de cumpleaños…) — #1765.
 *
 * La configuración vive en Nómina → Configuración; el saldo de cada ficha lo
 * calcula el backend (`saldo_de_beneficio`) y acá solo se muestra: no se guarda
 * una bolsa en ningún lado.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../../lib/apiClient'

export type PeriodoBeneficio = 'calendario' | 'aniversario'
export type AplicaA = 'todos' | 'asignados'
export type FechaDeReferencia = '' | 'cumpleanos' | 'aniversario_ingreso'
export type SiCaeEnInhabil = 'se_pierde' | 'habil_anterior_o_siguiente' | 'habil_siguiente'

export interface BeneficioDeDias {
  id: string
  nombre: string
  activo: boolean
  /** Decimal como texto («3», «0.50»). */
  dias_por_periodo: string
  periodo: PeriodoBeneficio
  proporcional_al_ingreso: boolean
  permite_medio_dia: boolean
  acumulable: boolean
  tope_acumulado: string | null
  aplica_a: AplicaA
  fecha_de_referencia: FechaDeReferencia
  ventana_antes: number
  ventana_despues: number
  si_cae_en_inhabil: SiCaeEnInhabil
  requiere_segundo_aprobador: boolean
  requiere_motivo: boolean
  asignaciones_count: number
}

export interface AsignacionDeBeneficio {
  id: string
  beneficio: string
  employee: string
  employee_name: string
  employee_position: string
  /** Reemplaza los días del beneficio para esta ficha; null = los del beneficio. */
  dias_por_periodo: string | null
  excluido: boolean
  nota: string
}

/** Saldo de un beneficio para una ficha (backend: `SaldoDeBeneficio.a_dict`). */
export interface SaldoDeBeneficio {
  beneficio: string
  nombre: string
  periodo_inicio: string
  periodo_fin: string
  asignado: string
  arrastre: string
  usados: string
  en_tramite: string
  disponible: string
  /** Lo aprobado que pasa lo asignado (p. ej. tras reclasificar el historial). */
  excedido: string
  permite_medio_dia: boolean
  requiere_motivo: boolean
  fecha_de_referencia: FechaDeReferencia
  fecha_de_referencia_display: string
  /** null = cualquier día hábil del período; [] = ninguno (o falta el dato en la ficha). */
  fechas_validas: string[] | null
}

export const PERIODO_LABEL: Record<PeriodoBeneficio, string> = {
  calendario: 'Año calendario',
  aniversario: 'Año desde la fecha de ingreso',
}
export const REFERENCIA_LABEL: Record<FechaDeReferencia, string> = {
  '': 'Ninguna (cualquier día hábil)',
  cumpleanos: 'Cumpleaños',
  aniversario_ingreso: 'Aniversario de ingreso',
}
export const INHABIL_LABEL: Record<SiCaeEnInhabil, string> = {
  se_pierde: 'Se pierde',
  habil_anterior_o_siguiente: 'Día hábil anterior o siguiente',
  habil_siguiente: 'Día hábil siguiente',
}

/** «3», «0,5», «1,5»: los días como se leen, sin ceros de más. */
export function formatoDias(v: string | number | null | undefined, locale = 'es-CL') {
  const n = parseFloat(String(v ?? 0))
  if (!isFinite(n)) return '—'
  return n.toLocaleString(locale, { maximumFractionDigits: 1 })
}

/** Una línea que resume la política: «3 días por año calendario · se pierden al cerrar…». */
export function resumenDelBeneficio(b: Pick<BeneficioDeDias,
  'dias_por_periodo' | 'periodo' | 'acumulable' | 'tope_acumulado' | 'permite_medio_dia'
  | 'proporcional_al_ingreso' | 'fecha_de_referencia' | 'aplica_a'>, locale = 'es-CL') {
  const dias = parseFloat(b.dias_por_periodo || '0')
  const partes = [
    `${formatoDias(dias, locale)} ${dias === 1 ? 'día' : 'días'} por ${
      b.periodo === 'calendario' ? 'año calendario' : 'año desde el ingreso'}`,
    b.acumulable
      ? (b.tope_acumulado ? `acumulable hasta ${formatoDias(b.tope_acumulado, locale)}` : 'acumulable')
      : 'lo no usado se pierde al cerrar el período',
  ]
  if (b.permite_medio_dia) partes.push('medios días permitidos')
  if (b.proporcional_al_ingreso) partes.push('proporcional al ingreso')
  if (b.fecha_de_referencia) partes.push(`en torno al ${REFERENCIA_LABEL[b.fecha_de_referencia].toLowerCase()}`)
  if (b.aplica_a === 'asignados') partes.push('solo fichas asignadas')
  return partes.join(' · ')
}

// ─── Configuración ──────────────────────────────────────────────────────────

export function useBeneficios() {
  return useQuery<BeneficioDeDias[]>({
    queryKey: ['payroll-beneficios'],
    queryFn: async () => (await apiClient.get('/payroll/beneficios/')).data,
  })
}

export function useGuardarBeneficio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...d }: Partial<BeneficioDeDias>) => (
      id
        ? await apiClient.patch(`/payroll/beneficios/${id}/`, d)
        : await apiClient.post('/payroll/beneficios/', d)
    ).data as BeneficioDeDias,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-beneficios'] })
      qc.invalidateQueries({ queryKey: ['employee-beneficios'] })
    },
  })
}

export function useBorrarBeneficio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/payroll/beneficios/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-beneficios'] }),
  })
}

// ─── Excepciones por ficha ──────────────────────────────────────────────────

export function useAsignaciones(beneficioId: string | null) {
  return useQuery<AsignacionDeBeneficio[]>({
    queryKey: ['payroll-beneficio-asignaciones', beneficioId],
    queryFn: async () => (await apiClient.get(`/payroll/beneficios/${beneficioId}/asignaciones/`)).data,
    enabled: !!beneficioId,
  })
}

export function useGuardarAsignacion(beneficioId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...d }: Partial<AsignacionDeBeneficio>) => (
      id
        ? await apiClient.patch(`/payroll/beneficios/${beneficioId}/asignaciones/${id}/`, d)
        : await apiClient.post(`/payroll/beneficios/${beneficioId}/asignaciones/`, d)
    ).data as AsignacionDeBeneficio,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-beneficio-asignaciones', beneficioId] })
      qc.invalidateQueries({ queryKey: ['payroll-beneficios'] })
      qc.invalidateQueries({ queryKey: ['employee-beneficios'] })
    },
  })
}

export function useBorrarAsignacion(beneficioId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      apiClient.delete(`/payroll/beneficios/${beneficioId}/asignaciones/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-beneficio-asignaciones', beneficioId] })
      qc.invalidateQueries({ queryKey: ['payroll-beneficios'] })
      qc.invalidateQueries({ queryKey: ['employee-beneficios'] })
    },
  })
}

// ─── Saldo de una ficha ─────────────────────────────────────────────────────

export interface BeneficiosDeLaFicha {
  results: SaldoDeBeneficio[]
  /** Beneficios activos que la ficha NO tiene (excluida o sin asignación). */
  sin_beneficio: { beneficio: string; nombre: string }[]
}

export function useBeneficiosDeLaFicha(employeeId: string | null | undefined) {
  return useQuery<BeneficiosDeLaFicha>({
    queryKey: ['employee-beneficios', employeeId],
    queryFn: async () => (await apiClient.get(`/payroll/employees/${employeeId}/beneficios/`)).data,
    enabled: !!employeeId,
  })
}
